import type { Activity } from '@/types/activity';
import { RotateCcw } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ActivityFilters } from './ActivityFilters';
import { SelectionPreview } from './SelectionPreview';
import { SelectionSummary } from './SelectionSummary';
import { usePatientDownloadController } from './usePatientDownloadController';
import type { DownloadMode } from './downloadWorkflow';

interface PatientDownloadScreenProps {
  activities: Activity[];
  isLoading: boolean;
  mode: DownloadMode;
}

export function PatientDownloadScreen({
  activities,
  isLoading,
  mode,
}: PatientDownloadScreenProps) {
  const controller = usePatientDownloadController({
    mode,
    activities,
  });
  const isDownloading =
    controller.downloadProgress.status === 'fetching' ||
    controller.downloadProgress.status === 'packaging';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Build Your Selection"
          description={controller.config.cardDescription}
          action={
            controller.hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={controller.clearFilters}
                leftIcon={<RotateCcw className="h-4 w-4" />}
              >
                Clear Filters
              </Button>
            )
          }
        />
        <CardBody className="space-y-6">
          <ActivityFilters
            activityTypes={controller.activityTypes}
            vibrationModes={controller.vibrationModes}
            dateRange={controller.dateRange}
            onActivityTypesChange={controller.setActivityTypes}
            onVibrationModesChange={controller.setVibrationModes}
            onDateRangeChange={controller.handleDateRangeChange}
          />
        </CardBody>
      </Card>

      <SelectionSummary
        totalActivities={controller.selectedActivities.length}
        totalPatients={1}
        onDownload={controller.handleDownload}
        isDownloading={isDownloading}
        progress={controller.downloadProgress}
      />

      {(controller.filteredActivities.length > 0 || controller.hasFilters) && (
        <Card>
          <CardHeader
            title="Preview"
            description={
              controller.hasFilters
                ? `${controller.filteredActivities.length} ${controller.filteredActivities.length === 1 ? 'activity' : 'activities'} match your filters`
                : `All ${activities.length} ${activities.length === 1 ? 'activity' : 'activities'}`
            }
          />
          <CardBody>
            <SelectionPreview
              activities={controller.filteredActivities}
              totalCount={controller.filteredActivities.length}
              isLoading={isLoading}
              pageSize={10}
              restrictUnavailableActivities={mode === 'processed'}
              selectedActivityIds={controller.selectedActivityIds}
              onToggleActivity={controller.toggleActivitySelection}
              onSelectAll={controller.selectAllActivities}
              onDeselectAll={controller.deselectAllActivities}
            />
          </CardBody>
        </Card>
      )}
    </div>
  );
}
